import React from "react";
import _ from "lodash";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import secureLocalStorage from "react-secure-storage";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Function to normalise the progress of loading elements
 * @param {number} value The current loading value of the element to be rendered
 * @param {number} minValue The minimum possible element loading value
 * @param {number} maxValue The maximum possible element loading value
 * @returns {number} The value of the progress indicator for the determinate and buffer variants
 */
export const normaliseProgressValues = (value, minValue, maxValue) => {
  if (
    _.some(
      [value, minValue, maxValue],
      (current) => _.isNil(current) || _.isNaN(current)
    )
  )
    return NaN;
  const denominator = maxValue - minValue;
  if (denominator === 0) return 0;
  return ((value - minValue) * 100) / denominator;
};

/**
 * Creates headers and formatted headers arrays from a table array.
 * Headers - headings in the original formatting;
 * Formatted headers – normalised headings in Capitalised Case
 * @param {Array<Object>} tableArray Array of the table from which to build headers
 * @returns {Object}
 */
export const getTableHeaders = (tableArray) => {
  const uniqueKeys = new Set();

  tableArray.forEach((obj) => {
    Object.keys(obj).forEach((key) => uniqueKeys.add(key));
  });
  const headers = Array.from(uniqueKeys);

  const formattedHeaders = headers.map((currentHeader) => {
    let result;
    const formattedHeader = currentHeader
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .split(/[_\s]+/)
      .map(
        (word) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase().trim()
      )
      .join(" ")
      .trim();
    switch (formattedHeader) {
      case "Id":
        result = "ID";
        break;
      case "Cid":
        result = "CID";
        break;
      default:
        result = formattedHeader;
        break;
    }
    return result;
  });

  return { headers, formattedHeaders };
};

/**
 * Converts number to percents
 * @param {number} number Number to be converted to a percentage
 * @param {number} digitsAfterComma How many digits should be after the decimal point
 * @returns {string} a string in a format like "20.5%"
 */
export const toPercents = (number, digitsAfterComma = 0) => {
  if (_.isNaN(number) || _.isNil(number))
    throw new Error("Invalid input: number must be a valid number");

  const percentage = number > 1 ? number : number * 100;

  return `${percentage.toFixed(digitsAfterComma)}%`;
};

/**
 * Converts the first letter of each word to uppercase.
 * Exceptions will stay in uppercase (as abbreviation).
 * The exceptions is: MS, ITBF, DC, IT.
 * @param {string} toConvert The string to be formatted
 * @param {string} separator Separator between words
 * @param {boolean} isNormalise Whether it is necessary to normalize the string. If true, a regular space will be used instead of all delimiters. If false, the delimiters will remain as they are
 * @returns {string} Formatted string value
 */
export const firstLettersToUpperCase = (
  toConvert,
  separator,
  isNormalise = false
) => {
  const exceptions = ["ms", "itbf", "dc", "it"];
  return toConvert
    .split(separator)
    .map((word) => {
      return exceptions.includes(word.toLowerCase().trim())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase().trim();
    })
    .join(isNormalise ? " " : separator)
    .trim();
};

/**
 * Calculates how much time is left until the time specified in the argument
 * @param {string|Date} targetDateTime Time to which you want to measure the distance
 * @param {boolean} [reverse=false] If true, calculates time elapsed since targetDateTime.
 * @returns {string} String in "hh:mm:ss" format
 */
export const timeToTarget = (targetDateTime, reverse = false) => {
  const fallbackMessage = "Cannot calculate time to target";

  if (_.isNil(targetDateTime) || _.isNaN(targetDateTime))
    return fallbackMessage;

  if (typeof targetDateTime?.getMonth != "function")
    targetDateTime = new Date(targetDateTime);

  const now = new Date(Date.now());
  const utcNow = new Date(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds()
  );

  const diff = reverse ? utcNow - targetDateTime : targetDateTime - utcNow;

  if (diff <= 0) return "0:00";

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  if (_.isNaN(hours) || _.isNaN(minutes) || _.isNaN(seconds))
    return fallbackMessage;

  return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
};

/**
 * The function isAnyEmpty checks if any of the input values are null or undefined and returns true if
 * any are found.
 * @param  {...any} values Values to be checked
 * @returns The function `isAnyEmpty` will return `true` if any of the values passed to it are `null`
 * or `undefined`, otherwise it will return 'false'
 */
export const isAnyEmpty = (...values) => {
  if (values.some((value) => value === null || value === undefined)) {
    return true;
  } else return false;
};

/**
 * The useCtrlHotkeyAction function in JavaScript React listens for specific Ctrl key combinations to
 * navigate through a target history array.
 * @param {string} hotkey The code of the key to be monitored (for example, ‘KeyZ’ or ‘KeyY’)
 * @param {number} pointer The current index in targetHistory
 * @param {Function} setPointer Function for updating the pointer
 * @param {Array} targetHistory An array of history within which the pointer is changed
 * @param {Function} setNewVersion Function of setting new version on undo / redo actions
 */
export const useCtrlHotkeyAction = (
  hotkey,
  pointer,
  setPointer,
  targetHistory,
  setNewVersion
) => {
  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.code === hotkey) {
        event.preventDefault();
        let newPointer;
        if (hotkey === "KeyZ") {
          newPointer = Math.max(pointer - 1, 0);
        } else if (hotkey === "KeyY") {
          newPointer = Math.min(pointer + 1, targetHistory.length - 1);
        }

        setPointer(newPointer);
        setNewVersion(targetHistory[newPointer]);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [pointer, targetHistory]);
};

/**
 * The `getCookie` function retrieves the value of a specific cookie by its name from the document's
 * cookies.
 * @param {string} name Name of the needed cookie
 * @returns The `getCookie` function returns the value of the cookie with the specified name if it
 * exists, otherwise it returns `null`.
 */
export const getCookie = (name) => {
  const cookies = document.cookie.split("; ");

  for (const cookie of cookies) {
    const [key, value] = cookie.split("=");
    if (key === name) {
      return decodeURIComponent(value);
    }
  }
  return null;
};

/**
 * The function `toAcronym` takes a string input and returns an abbreviation by taking the first
 * letter of each word and capitalizing it.
 * @param {string} str String to be converted to an abbreviation
 * @returns The `toAcronym` function takes a string as input and returns either the input string
 * in uppercase if it matches a specific pattern or returns an abbreviation of the input string by
 * taking the first letter of each word and converting it to uppercase.
 */
export const toAcronym = (str) => {
  if (!str || typeof str !== "string") return;

  if (/^[A-Z]+(\.[A-Z]+)*$/.test(str)) {
    return str;
  }

  return str
    .split(/\s+/)
    .map((word) => {
      const cleared = word.replace(/\W/g, "");
      return cleared.charAt(0).toUpperCase();
    })
    .join("");
};

/**
 * The function `a11yProps` generates accessibility props for a tab component in React.
 * @param {number} index Index of the current tab
 * @param {string} prefix Page-unique index
 * @returns The `a11yProps` function returns an object with two properties: `id` and `aria-controls`.
 * The `id` property is a string that combines the `prefix` (if provided) with the index to create a
 * unique ID for a tab element. The `aria-controls` property is a string that combines the `prefix` (if
 * provided) with the index to create a unique
 */
export const a11yProps = (index, prefix = "") => {
  if (typeof index !== "number" || _.isNaN(index)) {
    throw new Error("Tab index should be a valid number");
  }
  prefix = typeof prefix === "string" ? prefix : "";
  return {
    id: `${prefix ? `${prefix}-` : ""}tab-${index}`,
    "aria-controls": `${prefix ? `${prefix}-` : ""}tabpanel-${index}`,
  };
};

/**
 * The function `validatePasswordStrength` checks the strength of a password based on length, character
 * types, and special characters.
 * @param {string} password The password to validate
 * @returns The `validatePasswordStrength` function returns an object with two properties:
 * 1. `strength`: A string indicating the strength of the password, which can be "Weak", "Medium", or
 * "Strong" based on the validation criteria.
 * 2. `errors`: An array containing error messages if the password does not meet the specified
 * criteria.
 */
export const validatePasswordStrength = (password) => {
  if (typeof password !== "string")
    throw new Error("Password should be a correct strings");

  const errors = [];

  password.length < 8 &&
    errors.push("Password must be at least 8 characters long");

  !/[A-Z]/.test(password) &&
    errors.push("Password must contain at least one uppercase letter (A–Z)");

  !/[a-z]/.test(password) &&
    errors.push("Password must contain at least one lowercase letter (a–z)");

  !/\d/.test(password) &&
    errors.push("Password must contain at least one number (0–9)");

  !/[\W_]/.test(password) &&
    errors.push(
      "Password must contain at least one special character (!@#...)"
    );

  const errorCount = errors.length;

  let strength = "Weak";
  if (errorCount <= 2 && errorCount > 0) strength = "Medium";
  if (errorCount === 0) strength = "Strong";

  return {
    strength,
    errors,
  };
};

/**
 * The function `addDurationToCurrentTime` calculates the future time by adding a given duration to the
 * current time in the Europe/Kyiv timezone and returns it in a specific format.
 * @param {string} duration Duration of time needed to be added;
 * @returns {string} The function `addDurationToCurrentTime` takes a duration in the format "HH:mm:ss", adds it
 * to the current time in the Europe/Kyiv timezone, and returns the result in the format "MM/DD/YYYY,
 * HH:mm:ss".
 */
export const addDurationToCurrentTime = (duration) => {
  const [hh, mm, ss] = duration.split(":").map(Number);

  const nowKyiv = dayjs().tz("Europe/Kyiv");

  const resultTime = nowKyiv
    .add(hh, "hour")
    .add(mm, "minute")
    .add(ss, "second");

  return resultTime.format("MM/DD/YYYY, HH:mm:ss");
};

/**
 * The `buildQuery` function takes an object of parameters, filters out null or undefined values,
 * encodes the key-value pairs, and constructs a query string.
 * @param {Object} params params of the needed query;
 * @returns {string} the `buildQuery` function returns a query string constructed from the provided parameters
 * object. If the parameters object is not empty, it filters out any key-value pairs where the value is
 * null, encodes the key and value, and joins them with an ampersand (&) to form a query string. If the
 * resulting query string is not empty, it prepends a question mark (?) before the query
 */
export const buildQuery = (params) => {
  if (typeof params !== "object" || _.isNil(params) || Array.isArray(params))
    throw new Error("Params should be an object type");

  const query = Object.entries(params)
    .filter(([_, value]) => value != null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");

  return query ? `?${query}` : "";
};

export const nativeJoinArray = (array) => {
  if (!Array.isArray(array)) throw new Error("Not an array passed as param");

  let result = "";

  array.forEach((item, index) => {
    const isLast = index === array.length - 1;
    const isSecondLast = index === array.length - 2;

    result += item;
    if (array.length > 1) result += isSecondLast ? " or " : !isLast ? ", " : "";
  });

  return result;
};

/**
 * Gets a value from secureLocalStorage by a key encoded in base64.
 * @param {string} key Key to read the value (original, not encoded);
 * @returns {string | null} The value stored under this key, or null if not present.
 */
export const getLocalStorageValue = (key) => {
  return secureLocalStorage.getItem(btoa(key));
};

/**
 * Writes the value to secureLocalStorage under a base64-encoded key.
 * Throws an error if the value is not a string, number, boolean, or object,
 * or is null/undefined/NaN.
 * @param {string} key Key to read the value (original, not encoded);
 * @param {string | number | boolean | object} value Value to save;
 * @throws {Error} If the value does not match the expected types.
 */
export const setLocalStorageValue = (key, value) => {
  if (
    _.isNil(value) ||
    _.isNaN(value) ||
    !["string", "number", "boolean", "object"].includes(typeof value)
  )
    throw new Error(
      "Invalid input: value must be a valid number, string, object, or boolean"
    );
  secureLocalStorage.setItem(btoa(key), value);
};

export const capitalizeFirstLetter = (string) => {
  if (typeof string !== "string" || string.length === 0) {
    throw new Error("Param should be a valid string");
  }
  return string.charAt(0).toUpperCase() + string.slice(1);
};
